import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { parseAlphabetWritingWordLayoutPolicy } from '@crabs-ai/alphabet-writing-runtime/contracts';
import {
  loadAlphabetWritingPlayback,
  readAlphabetWritingPlayback,
} from '@crabs-ai/alphabet-writing-runtime/browser';
import {
  AbsoluteFill,
  cancelRender,
  continueRender,
  delayRender,
  getRemotionEnvironment,
  staticFile,
  useCurrentFrame,
  useDelayRender,
  useRemotionEnvironment,
  useVideoConfig,
} from 'remotion';
import {
  AUTHORING_WRITING_GUIDE_PLAYBACK_CONTRACT,
  isAuthoringWritingGuideCodeItem,
  isWordChantCodeItem,
  type CodeItem,
} from './code-item-type';
import {
  readEmbeddedAuthoringRendererSource,
  readEmbeddedAuthoringRuntime,
} from './authoring-embedded-runtime';
import {
  assertApprovedWritingGuideRuntimeMatchesRelease,
  buildApprovedWritingGuideRenderRuntime,
  buildWritingGuideCompositeRuntimeSource,
  resolveApprovedWritingGuideRelease,
  type ApprovedWritingGuideRelease,
} from './approved-writing-guide-release';
import { WRITING_GUIDE_RENDER_HOST_CONTRACT } from './writing-guide-render-host-contract';
import {readTrustedWordChantSpec} from '@waka/compat/trusted-word-chant';
import {isCanonicalStoryCandidate,readTrustedCanonicalStory} from '@waka/compat/trusted-canonical-story';
import { useCodeCompilation } from '@/shared/lib/remotion';
import {CodeClockBoundary} from '@waka/compat/code-clock-boundary';
import {calculateFadeInOpacity,calculateFadeOutOpacity} from '../video/calculate-fade';
import {readTrustedWritingGuideSpec} from '@waka/compat/trusted-writing-guide';
import {loadTrustedWritingPlayback, type TrustedWritingPlayback} from '@waka/compat/trusted-writing-guide-playback';
import {loadBundleWritingGuideRuntimeAsset} from '@waka/compat/bundle-writing-guide-runtime';

type WordChantComponent = typeof import('@/features/playground/word-chant-remotion/WordChantComposition').WordChantComposition;
type StoryComponent = typeof import('@/shared/lib/remotion/ssul-templates/StoryTemplate').StoryTemplateComp;
let readyStoryComponent: StoryComponent | null = null;
let storyLoadPromise: Promise<StoryComponent> | null = null;
function loadStoryTemplate(): Promise<StoryComponent> {
  if (readyStoryComponent) return Promise.resolve(readyStoryComponent);
  if (!storyLoadPromise) storyLoadPromise = import('@/shared/lib/remotion/ssul-templates/StoryTemplate')
    .then(module => {
      readyStoryComponent = module.StoryTemplateComp;
      return module.StoryTemplateComp;
    }).catch(error => { storyLoadPromise = null; throw error; });
  return storyLoadPromise;
}
let readyWordChantComponent: WordChantComponent | null = null;
let wordChantLoadPromise: Promise<WordChantComponent> | null = null;

function loadWordChantComposition(): Promise<WordChantComponent> {
  if (readyWordChantComponent) return Promise.resolve(readyWordChantComponent);
  if (!wordChantLoadPromise) {
    wordChantLoadPromise = import('@/features/playground/word-chant-remotion/WordChantComposition')
      .then(async module => {
        await module.prepareWordChantFonts();
        readyWordChantComponent = module.WordChantComposition;
        return module.WordChantComposition;
      })
      .catch(error => {
        wordChantLoadPromise = null;
        throw error;
      });
  }
  return wordChantLoadPromise;
}

const WordChantComposition: React.FC<{
  spec: NonNullable<ReturnType<typeof readTrustedWordChantSpec>>;
  includeAudio: boolean;
  audioOnly?: boolean;
}> = (props) => {
  const delays = useDelayRender();
  const [Component, setComponent] = useState<WordChantComponent | null>(() => readyWordChantComponent);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const renderHandle = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (Component) return;
    let active = true;
    const handle = delays.delayRender('Loading fixed Word Chant renderer and fonts');
    renderHandle.current = handle;
    void loadWordChantComposition().then(Loaded => {
      if (active) setComponent(() => Loaded);
    }).catch(reason => {
      if (!active) return;
      const error = reason instanceof Error ? reason : new Error('Word Chant renderer failed to load');
      setLoadError(error);
      delays.cancelRender(error);
    });
    return () => {
      active = false;
      if (renderHandle.current !== null) {
        delays.continueRender(renderHandle.current);
        renderHandle.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (Component && renderHandle.current !== null) {
      delays.continueRender(renderHandle.current);
      renderHandle.current = null;
    }
  }, [Component]);

  if (loadError) throw loadError;
  return Component ? <Component {...props} /> : null;
};

const AUTHORING_ENGINE_URL =
  '/agent-workflow/alphabet-authoring/runtime/authoring-playback-engine.js';
const DEFAULT_AUTHORING_DURATION_SEC = 5;
const DEFAULT_AUTHORING_FPS = 30;

interface AuthoringPlaybackApi {
  playbackContract: typeof AUTHORING_WRITING_GUIDE_PLAYBACK_CONTRACT;
  version: string;
  frameToProgress?(frame: number, totalFrames: number): number;
  renderWordFrameToCanvas(
    canvas: HTMLCanvasElement,
    runtime: Record<string, unknown>,
    options: Record<string, unknown>,
  ): unknown;
}

interface AuthoringWritingGuideSpec {
  approvedReleases: ApprovedWritingGuideRelease[];
  contractError: string | null;
  durationSec: number;
  fps: number;
  layoutPolicy: Record<string, unknown> | null;
  layoutOverride: unknown;
  runtime: Record<string, unknown>;
  strokeTimeline: unknown[];
  style: Record<string, unknown>;
  timelineOptions: Record<string, unknown>;
  word: string;
}

const approvedRuntimeAssetPromises = new Map<
  string,
  Promise<Record<string, unknown>>
>();
const MAX_APPROVED_LEGACY_RUNTIME_BYTES = 2 * 1024 * 1024;

async function readBoundedApprovedRuntimeBytes(response: Response): Promise<ArrayBuffer> {
  if (!response.body) throw Error('승인된 따라쓰기 runtime 응답이 비어 있습니다.');
  const reader=response.body.getReader();
  const parts:Uint8Array[]=[];
  let size=0;
  try {
    while (true) {
      const {done,value}=await reader.read();
      if (done) break;
      size+=value.byteLength;
      if (size>MAX_APPROVED_LEGACY_RUNTIME_BYTES) {
        await reader.cancel();
        throw Error('승인된 따라쓰기 runtime 크기가 허용 범위를 넘었습니다.');
      }
      parts.push(value);
    }
  } finally { reader.releaseLock(); }
  const result=new Uint8Array(new ArrayBuffer(size));
  let offset=0;
  for (const part of parts) { result.set(part,offset); offset+=part.byteLength; }
  return result.buffer;
}

/**
 * CodeItem을 캔버스에 렌더링하는 레이어 컴포넌트.
 * LLM이 생성한 코드를 JIT 컴파일하여 실시간 프리뷰를 표시합니다.
 *
 * 2가지 렌더 모드:
 * - `boundingMode === 'fullscreen'` (기본): 코드가 AbsoluteFill 로 캔버스 전체를 채우고,
 *   item.left/top 만 transform 으로 이동시켜 선택 테두리와 동기화.
 * - `boundingMode === 'bounded'`: wrap div 를 item.left/top/width/height 에 정확히 맞춰
 *   크기를 제한하고 overflow:hidden 적용. 캔버스에서 drag/resize 가능.
 *   이 경우 코드 안의 AbsoluteFill 은 wrap div 크기를 채움.
 */
export const CodeLayer: React.FC<{
  item: CodeItem;
  trackMuted: boolean;
  audioOnly?: boolean;
  zIndex?: number;
}> = ({ item, trackMuted, audioOnly=false, zIndex }) => {
  const frame=useCurrentFrame(),{fps}=useVideoConfig();
  const opacity=item.opacity*calculateFadeInOpacity({currentFrame:frame,framesPerSecond:fps,fadeInDurationInSeconds:item.fadeInDurationInSeconds,fadeWindow:item.visualFadeWindow})*calculateFadeOutOpacity({currentFrame:frame,framesPerSecond:fps,totalDurationInFrames:item.durationInFrames,fadeOutDurationInSeconds:item.fadeOutDurationInSeconds,fadeWindow:item.visualFadeWindow});
  return <div style={{position:'absolute',inset:0,opacity,zIndex}}><CodeClockBoundary item={item}><CodeContent item={item} trackMuted={trackMuted} audioOnly={audioOnly} zIndex={zIndex}/></CodeClockBoundary></div>;
};

const CodeContent:React.FC<{item:CodeItem;trackMuted:boolean;audioOnly:boolean;zIndex?:number}>=({item,trackMuted,audioOnly,zIndex})=>{
  const environment = useRemotionEnvironment();
  if (isAuthoringWritingGuideCodeItem(item)) {
    if (audioOnly) return null;
    if (environment.isClientSideRendering) return <TrustedWritingGuideCodeLayer item={item} zIndex={zIndex}/>;
    return (
      <AuthoringWritingGuideCodeLayer
        item={item}
        zIndex={zIndex}
      />
    );
  }
  if (isWordChantCodeItem(item)) {
    return (
      <WordChantCodeLayer
        item={item}
        trackMuted={trackMuted}
        audioOnly={audioOnly}
        zIndex={zIndex}
      />
    );
  }
  if (isCanonicalStoryCandidate(item.code)) {
    return <CanonicalStoryCodeLayer item={item} audioOnly={audioOnly} zIndex={zIndex} />;
  }

  return (
    <CompiledCodeLayer
      item={item}
      zIndex={zIndex}
    />
  );
};

function TrustedWritingGuideCodeLayer({item,zIndex}:{item:CodeItem;zIndex?:number}) {
  const spec = useMemo(() => readTrustedWritingGuideSpec(item), [item]);
  const frame = useCurrentFrame();
  const video = useVideoConfig();
  const delay = useDelayRender();
  const environment = useRemotionEnvironment();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [prepared,setPrepared] = useState<{key:object;runtime:Record<string,unknown>;playback:TrustedWritingPlayback}|null>(null);
  const [error,setError] = useState<Error|null>(null);
  const key = useMemo(() => ({}), [spec,frame,video.width,video.height,video.fps]);
  const pending = useRef<{key:object;handle:number;timer:ReturnType<typeof setTimeout>|null}|null>(null);
  const readyKey = useRef<object|null>(null);
  const finish = (success=false) => {
    if (pending.current?.key === key) {
      if (pending.current.timer) clearTimeout(pending.current.timer);
      delay.continueRender(pending.current.handle);
      pending.current = null;
      if (success) readyKey.current = key;
    }
  };
  useLayoutEffect(() => {
    if (readyKey.current === key) return;
    pending.current = {key,handle:delay.delayRender('승인된 따라쓰기 프레임 준비'),timer:null};
    let active = true;
    const fail = (reason:unknown) => {
      if (!active) return;
      const failure = reason instanceof Error ? reason : Error('승인된 따라쓰기 프레임을 만들지 못했습니다.');
      setError(failure);
      if (environment.isClientSideRendering || environment.isRendering) delay.cancelRender(failure);
      finish();
    };
    pending.current.timer = setTimeout(() => fail(Error('승인된 따라쓰기 프레임 준비 시간이 초과됐습니다.')), 60_000);
    // Verify the bundle manifest, runtime, policy and project glyphs before
    // executing even a byte-verified renderer script.
    void prepareAuthoringRenderRuntime({...spec,approvedReleases:spec.releases,contractError:null})
      .then(async runtime => {
        if (!active) return null;
        return {
          runtime,
          playback: await loadTrustedWritingPlayback(spec.releases[0]!, staticFile(spec.releases[0]!.rendererAssetPath)),
        };
      }).then(result => {
      if (!result) return;
      const {runtime,playback} = result;
      if (active) setPrepared({key,runtime,playback});
    }).catch(fail);
    return () => { active=false; finish(); };
  }, [key]);
  useLayoutEffect(() => {
    if (prepared?.key !== key || !canvas.current) return;
    try {
      const totalFrames = Math.max(1,Math.round(spec.durationSec * spec.fps));
      const progress = frameToAuthoringProgress(prepared.playback,frame,totalFrames);
      if (!Number.isFinite(progress)) throw Error('따라쓰기 프레임 시간이 올바르지 않습니다.');
      prepared.playback.renderWordFrameToCanvas(canvas.current,prepared.runtime,{
        ...spec.style,
        durationSec:spec.durationSec,
        fps:spec.fps,
        height:video.height,
        layout:spec.layoutPolicy,
        layoutOverride:spec.layoutOverride,
        progress,
        showFourLineGuides:false,
        showGuideOverlay:false,
        showOriginalGuideOverlay:spec.style.showOriginalGuideOverlay !== false,
        strokeTimeline:spec.strokeTimeline,
        timelineOptions:spec.timelineOptions,
        width:video.width,
        word:spec.word,
      });
      if (!canvas.current.getContext('2d') || canvas.current.width < 1 || canvas.current.height < 1)
        throw Error('따라쓰기 캔버스를 사용할 수 없습니다.');
      finish(true);
    } catch (reason) {
      const failure=reason instanceof Error?reason:Error('승인된 따라쓰기 프레임을 만들지 못했습니다.');
      setError(failure);
      if (environment.isClientSideRendering || environment.isRendering) delay.cancelRender(failure);
      finish();
    }
  }, [prepared,key]);
  if (error) throw error;
  return <div style={getCodeLayerPositionStyle(item,zIndex)}>
    <canvas ref={canvas} width={video.width} height={video.height} style={{width:'100%',height:'100%'}} />
  </div>;
}

function CanonicalStoryCodeLayer({item,audioOnly,zIndex}:{item:CodeItem;audioOnly:boolean;zIndex?:number}) {
  const data=useMemo(()=>readTrustedCanonicalStory(item),[item.code]);
  const {delayRender:scopedDelayRender,continueRender:scopedContinueRender,cancelRender:scopedCancelRender}=useDelayRender();
  const [Component,setComponent]=useState<StoryComponent|null>(()=>readyStoryComponent);
  const [loadError,setLoadError]=useState<Error|null>(null);
  const handle=useRef<number|null>(null);
  if(data&&!Component&&!loadError&&!audioOnly&&handle.current===null)
    handle.current=scopedDelayRender('Loading fixed StoryTemplateComp renderer and fonts');
  useLayoutEffect(()=>{
    if(!data||Component||audioOnly)return;
    let active=true;
    const pending=handle.current;
    const timer=setTimeout(()=>{
      if(!active)return;
      const error=new Error('StoryTemplateComp renderer or font loading timed out');
      setLoadError(error);
      scopedCancelRender(error);
    },15000);
    void loadStoryTemplate().then(Loaded=>{
      if(active)setComponent(()=>Loaded);
    }).catch(reason=>{
      if(!active)return;
      const error=reason instanceof Error?reason:new Error('StoryTemplateComp renderer failed to load');
      setLoadError(error);
      scopedCancelRender(error);
    });
    return()=>{
      active=false;clearTimeout(timer);
      if(pending!==null&&handle.current===pending){scopedContinueRender(pending);handle.current=null;}
    };
  },[data,Component,audioOnly,scopedCancelRender,scopedContinueRender]);
  useLayoutEffect(()=>{
    if(Component&&handle.current!==null){scopedContinueRender(handle.current);handle.current=null;}
  },[Component,scopedContinueRender]);
  if(audioOnly)return null; // The canonical wrapper supplies only data: StoryTemplateComp emits no audio.
  if(loadError)throw loadError;
  if(!data)return <div style={getCodeLayerPositionStyle(item,zIndex)}>StoryTemplateComp code is invalid for browser rendering.</div>;
  return <div style={getCodeLayerPositionStyle(item,zIndex)}>{Component?<Component data={data}/>:null}</div>;
}

function WordChantCodeLayer({
  item,
  trackMuted,
  audioOnly,
  zIndex,
}: {
  item: CodeItem;
  trackMuted: boolean;
  audioOnly: boolean;
  zIndex?: number;
}) {
  const spec = useMemo(
    () => readTrustedWordChantSpec(item),
    [item.templateId, item.wordChantSpec],
  );
  if (!spec) {
    if (audioOnly) return null;
    return (
      <div style={getCodeLayerPositionStyle(item, zIndex)}>
        <AbsoluteFill style={{display:'flex',alignItems:'center',justifyContent:'center',backgroundColor:'rgba(26, 26, 46, 0.9)',color:'#ff6b6b'}}>
          Word Chant render spec is invalid.
        </AbsoluteFill>
      </div>
    );
  }

  if (audioOnly) return <WordChantComposition spec={spec} includeAudio={!trackMuted} audioOnly />;

  return (
    <div style={getCodeLayerPositionStyle(item, zIndex)}>
      <WordChantComposition
        spec={spec}
        includeAudio={!trackMuted}
      />
    </div>
  );
}

function CompiledCodeLayer({
  item,
  zIndex,
}: {
  item: CodeItem;
  zIndex?: number;
}) {
  const { Component, error } = useCodeCompilation(item.code);

  const positionStyle: React.CSSProperties = useMemo(
    () => getCodeLayerPositionStyle(item, zIndex),
    [item, zIndex],
  );

  if (error || item.compiledError) {
    const errorMessage = error || item.compiledError || 'Unknown error';
    return (
      <div style={positionStyle}>
        <AbsoluteFill
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(26, 26, 46, 0.9)',
            padding: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              maxWidth: '80%',
            }}
          >
            <div
              style={{
                fontSize: 32,
                color: '#ff6b6b',
                fontWeight: 'bold',
              }}
            >
              Compile Error
            </div>
            <div
              style={{
                fontSize: 14,
                color: '#e0e0e0',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                padding: '12px 16px',
                borderRadius: 8,
                wordBreak: 'break-word',
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              {errorMessage}
            </div>
          </div>
        </AbsoluteFill>
      </div>
    );
  }

  if (!Component) {
    return (
      <div style={positionStyle}>
        <AbsoluteFill
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(26, 26, 46, 0.5)',
          }}
        >
          <div style={{ fontSize: 18, color: '#888' }}>Loading...</div>
        </AbsoluteFill>
      </div>
    );
  }

  // GTS_STEP M 씬: renderer timing SSOT(`timeline`)와 legacy `timingOverrides`를
  // generated component에 forward한다.
  // 미리보기(MSceneRemotionPreview / ScenesSequencePreview) 와 동일한 conditional spread 패턴 —
  // 해당 prop 을 받지 않는 일반 LTS/MTS chunk 컴포넌트에는 안전하게 noop.
  // useCodeCompilation 결과 Component 는 `(props: {}) => ...` 시그너처라 spread 시 타입이 맞지 않으므로
  // `Record<string, unknown>` 으로 cast (미리보기 측 MSceneRemotionPreview.tsx 와 동일 패턴).
  const componentProps: Record<string, unknown> = {
    ...(item.timeline ? { timeline: item.timeline } : {}),
    ...(item.timingOverrides ? { timingOverrides: item.timingOverrides } : {}),
  };

  return (
    <div style={positionStyle}>
      <Component {...componentProps} />
    </div>
  );
}

export function AuthoringWritingGuideCodeLayer({
  item,
  zIndex,
}: {
  item: CodeItem;
  zIndex?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const errorMessageRef = useRef<string | null>(null);
  const currentFrame = useCurrentFrame();
  const videoConfig = useVideoConfig();
  const spec = useMemo(() => readAuthoringWritingGuideSpec(item), [item]);
  const primaryApprovedRelease = spec?.approvedReleases[0] ?? null;
  const rendererVersion =
    primaryApprovedRelease?.rendererVersion ??
    item.rendererVersion ??
    readRecordString(spec?.runtime, 'rendererVersion');
  const rendererSourceHash =
    primaryApprovedRelease?.rendererSourceHash ??
    item.rendererSourceHash ??
    readRecordString(spec?.runtime, 'rendererSourceHash');
  const rendererAssetPath =
    primaryApprovedRelease?.rendererAssetPath ?? item.rendererAssetPath ?? null;
  const embeddedRendererSource = useMemo(
    () =>
      item.authoringSpec || item.rendererAssetPath
        ? null
        : readEmbeddedAuthoringRendererSource(item.code),
    [item.authoringSpec, item.code, item.rendererAssetPath],
  );
  const [scriptState, setScriptState] = useState<
    'loading' | 'loaded' | 'failed'
  >(() =>
    readAuthoringPlayback(rendererVersion, rendererSourceHash)
      ? 'loaded'
      : 'loading',
  );
  const [renderRuntime, setRenderRuntime] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const positionStyle: React.CSSProperties = useMemo(
    () => getCodeLayerPositionStyle(item, zIndex),
    [item, zIndex],
  );

  useEffect(() => {
    let isActive = true;
    const renderHandle = getRemotionEnvironment().isRendering
      ? delayRender('Loading immutable alphabet authoring renderer')
      : null;
    let renderHandleSettled = false;
    const continueOnce = () => {
      if (renderHandle === null || renderHandleSettled) return;
      renderHandleSettled = true;
      continueRender(renderHandle);
    };
    if (!spec) {
      setRenderRuntime(null);
      setScriptState('loaded');
      continueOnce();
      return;
    }
    if (spec.contractError) {
      setRenderRuntime(null);
      errorMessageRef.current = spec.contractError;
      setScriptState('failed');
      setErrorMessage(spec.contractError);
      if (renderHandle !== null && !renderHandleSettled) {
        renderHandleSettled = true;
        cancelRender(new Error(spec.contractError));
      }
      return;
    }

    setScriptState('loading');
    setRenderRuntime(null);
    setErrorMessage(null);
    Promise.all([
      prepareAuthoringRenderRuntime(spec),
      loadAuthoringPlaybackScript(
        rendererSourceHash,
        rendererAssetPath,
        rendererVersion,
        embeddedRendererSource,
      ),
    ])
      .then(([runtime]) => {
        if (isActive) {
          setRenderRuntime(runtime);
          setScriptState('loaded');
        }
        continueOnce();
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        const message =
          error instanceof Error
            ? error.message
            : '따라쓰기 전용 렌더러를 불러오지 못했습니다.';
        errorMessageRef.current = message;
        setScriptState('failed');
        setErrorMessage(message);
        if (renderHandle !== null && !renderHandleSettled) {
          renderHandleSettled = true;
          cancelRender(error instanceof Error ? error : new Error(message));
        }
      });

    return () => {
      isActive = false;
      continueOnce();
    };
  }, [
    embeddedRendererSource,
    rendererAssetPath,
    rendererSourceHash,
    rendererVersion,
    spec,
  ]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !spec || !renderRuntime || scriptState !== 'loaded') return;

    const playback = readAuthoringPlayback(rendererVersion, rendererSourceHash);
    if (!playback) {
      const message = '따라쓰기 전용 렌더러가 준비되지 않았습니다.';
      errorMessageRef.current = message;
      setErrorMessage(message);
      if (getRemotionEnvironment().isRendering) {
        cancelRender(new Error(message));
      }
      return;
    }

    const fps = Math.max(1, spec.fps || videoConfig.fps);
    const durationSec = Math.max(1, spec.durationSec);
    const totalFrames = Math.max(1, Math.round(durationSec * fps));
    const progress = frameToAuthoringProgress(
      playback,
      currentFrame,
      totalFrames,
    );

    try {
      playback.renderWordFrameToCanvas(canvas, renderRuntime, {
        ...spec.style,
        durationSec,
        fps,
        height: videoConfig.height,
        layout: spec.layoutPolicy ?? undefined,
        layoutOverride: spec.layoutOverride,
        progress,
        showFourLineGuides: false,
        showGuideOverlay: false,
        showOriginalGuideOverlay: spec.style.showOriginalGuideOverlay !== false,
        strokeTimeline: spec.strokeTimeline,
        timelineOptions: spec.timelineOptions,
        width: videoConfig.width,
        word: spec.word,
      });
      if (errorMessageRef.current !== null) {
        errorMessageRef.current = null;
        setErrorMessage(null);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : '따라쓰기 전용 렌더링에 실패했습니다.';
      errorMessageRef.current = message;
      setErrorMessage(message);
      if (getRemotionEnvironment().isRendering) {
        cancelRender(error instanceof Error ? error : new Error(message));
      }
    }
  }, [
    currentFrame,
    renderRuntime,
    rendererSourceHash,
    rendererVersion,
    scriptState,
    spec,
    videoConfig.fps,
    videoConfig.height,
    videoConfig.width,
  ]);

  if (!spec) {
    return (
      <AuthoringStaticCodeLayer
        item={item}
        zIndex={zIndex}
      />
    );
  }

  return (
    <div style={positionStyle}>
      <canvas
        ref={canvasRef}
        width={Math.max(1, videoConfig.width)}
        height={Math.max(1, videoConfig.height)}
        style={{
          height: '100%',
          width: '100%',
        }}
      />
      {scriptState === 'failed' || errorMessage ? (
        <AbsoluteFill
          style={{
            alignItems: 'center',
            backgroundColor: 'rgba(26, 26, 46, 0.85)',
            color: '#ffb4b4',
            display: 'flex',
            fontSize: 14,
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
          }}
        >
          {errorMessage ?? '따라쓰기 전용 렌더러를 불러오지 못했습니다.'}
        </AbsoluteFill>
      ) : null}
    </div>
  );
}

function AuthoringStaticCodeLayer({
  item,
  zIndex,
}: {
  item: CodeItem;
  zIndex?: number;
}) {
  const positionStyle: React.CSSProperties = useMemo(
    () => getCodeLayerPositionStyle(item, zIndex),
    [item, zIndex],
  );
  const backgroundColor = readAuthoringSolidBackgroundColor(item.code);

  return (
    <div style={positionStyle}>
      {backgroundColor ? <AbsoluteFill style={{ backgroundColor }} /> : null}
    </div>
  );
}

function getCodeLayerPositionStyle(
  item: CodeItem,
  zIndex?: number,
): React.CSSProperties {
  const isBounded = item.boundingMode === 'bounded';
  if (isBounded) {
    return {
      position: 'absolute',
      left: item.left,
      top: item.top,
      width: item.width,
      height: item.height,
      // 'visible' 로 허용: 모션/텍스트가 bounded 영역 밖으로 나가도 잘리지 않도록.
      // AI 가 생성한 chunk 의 애니메이션이 초기 rect 밖에서도 자연스럽게 보이도록 의도.
      overflow: 'visible',
      zIndex,
      pointerEvents: 'none' as const,
    };
  }

  return {
    position: 'absolute',
    inset: 0,
    transform: `translate(${item.left}px, ${item.top}px)`,
    zIndex,
    pointerEvents: 'none' as const,
  };
}

export function readAuthoringWritingGuideSpec(
  item: CodeItem,
): AuthoringWritingGuideSpec | null {
  const structuredSpec = item.authoringSpec;
  if (structuredSpec?.version === 'alphabet.authoring_render_spec.v1') {
    return {
      approvedReleases: [],
      contractError:
        '승인된 따라쓰기 재생에는 v2 렌더 명세가 필요합니다. 프로젝트를 다시 생성해 주세요.',
      durationSec:
        readPositiveNumber(item.durationSec) ?? DEFAULT_AUTHORING_DURATION_SEC,
      fps: readPositiveNumber(item.fps) ?? DEFAULT_AUTHORING_FPS,
      layoutPolicy: null,
      layoutOverride: structuredSpec.layoutOverride ?? null,
      runtime: structuredSpec.runtime ?? {},
      strokeTimeline: Array.isArray(structuredSpec.strokeTimeline)
        ? structuredSpec.strokeTimeline
        : [],
      style: {
        ...(structuredSpec.style ?? {}),
        ...(item.authoringStyle ?? {}),
      },
      timelineOptions: structuredSpec.timelineOptions ?? {},
      word: structuredSpec.word.trim(),
    };
  }

  if (structuredSpec?.version === 'alphabet.authoring_render_spec.v2') {
    const runtime = structuredSpec.runtime;
    const word = structuredSpec.word.trim();
    const layoutPolicy = readAuthoringWordLayoutPolicy(
      structuredSpec.layoutPolicy,
    );
    let approvedReleases: ApprovedWritingGuideRelease[] = [];
    let contractError: string | null = null;
    if (!runtime || typeof runtime !== 'object' || Array.isArray(runtime)) {
      contractError = '따라쓰기 구조화 렌더 명세에 런타임이 없습니다.';
    } else if (!layoutPolicy) {
      contractError =
        '따라쓰기 구조화 렌더 명세의 레이아웃 정책이 올바르지 않습니다.';
    } else {
      try {
        approvedReleases = resolveApprovedWritingGuideRelease(item, {
          ...structuredSpec,
          word,
        }).releases;
      } catch (error) {
        contractError =
          error instanceof Error
            ? error.message
            : '따라쓰기 승인 릴리스 계약을 확인하지 못했습니다.';
      }
    }
    return {
      approvedReleases,
      contractError,
      durationSec:
        readPositiveNumber(item.durationSec) ?? DEFAULT_AUTHORING_DURATION_SEC,
      fps: readPositiveNumber(item.fps) ?? DEFAULT_AUTHORING_FPS,
      layoutPolicy,
      layoutOverride: structuredSpec.layoutOverride ?? null,
      runtime,
      strokeTimeline: Array.isArray(structuredSpec.strokeTimeline)
        ? structuredSpec.strokeTimeline
        : [],
      style: {
        ...(structuredSpec.style ?? {}),
        ...(item.authoringStyle ?? {}),
      },
      timelineOptions: structuredSpec.timelineOptions ?? {},
      word,
    };
  }

  const runtime = readEmbeddedAuthoringRuntime(item.code);
  if (!runtime) {
    if (item.rendererSourceHash || item.rendererAssetPath) {
      return {
        approvedReleases: [],
        contractError:
          '따라쓰기 구조화 렌더 명세가 없어 재생 및 내보내기를 중단했습니다.',
        durationSec:
          readPositiveNumber(item.durationSec) ??
          DEFAULT_AUTHORING_DURATION_SEC,
        fps: readPositiveNumber(item.fps) ?? DEFAULT_AUTHORING_FPS,
        layoutPolicy: null,
        layoutOverride: null,
        runtime: {},
        strokeTimeline: [],
        style: { ...(item.authoringStyle ?? {}) },
        timelineOptions: {},
        word: '',
      };
    }
    return null;
  }

  // 구조화 spec 이전 프로젝트는 릴리스 메타데이터가 함께 저장되어 있어도
  // 내장 runtime/renderer를 그대로 사용한다. 서버 export 계약이 동일한
  // 내장 바이트 해시를 승인 릴리스 목록과 다시 검증한다.
  return {
    approvedReleases: [],
    contractError: readAuthoringContractError(item, runtime),
    durationSec:
      readPositiveNumber(item.durationSec) ??
      readEmbeddedRenderNumber(item.code, 'durationSec') ??
      DEFAULT_AUTHORING_DURATION_SEC,
    fps:
      readPositiveNumber(item.fps) ??
      readEmbeddedRenderNumber(item.code, 'fps') ??
      DEFAULT_AUTHORING_FPS,
    layoutPolicy: null,
    layoutOverride: readConstJsonValue(item.code, 'layoutOverride'),
    runtime,
    strokeTimeline: readArrayConst(item.code, 'strokeTimeline'),
    style: {
      ...(readRecordConst(item.code, 'style') ?? {}),
      ...(item.authoringStyle ?? {}),
    },
    timelineOptions: readRecordConst(item.code, 'timelineOptions') ?? {},
    word: readStringConst(item.code, 'word') ?? 'apple',
  };
}

function readAuthoringWordLayoutPolicy(
  value: unknown,
): Record<string, unknown> | null {
  try {
    return parseAlphabetWritingWordLayoutPolicy(value) as unknown as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

function readAuthoringContractError(
  item: CodeItem,
  runtime: Record<string, unknown>,
): string | null {
  const runtimeRendererHash =
    typeof runtime.rendererSourceHash === 'string'
      ? runtime.rendererSourceHash
      : null;
  if (
    item.rendererSourceHash &&
    runtimeRendererHash &&
    item.rendererSourceHash !== runtimeRendererHash
  ) {
    return '따라쓰기 런타임과 렌더러 해시가 일치하지 않습니다.';
  }
  if (!item.rendererAssetPath) return null;
  if (runtimeRendererHash !== item.rendererSourceHash) {
    return '따라쓰기 불변 렌더러 계약의 해시가 누락되었습니다.';
  }
  if (runtime.rendererAssetPath !== item.rendererAssetPath) {
    return '따라쓰기 불변 렌더러 경로가 일치하지 않습니다.';
  }
  const release =
    runtime.release && typeof runtime.release === 'object'
      ? (runtime.release as Record<string, unknown>)
      : null;
  if (release?.status !== 'approved') {
    return '승인된 따라쓰기 릴리스만 재생하고 내보낼 수 있습니다.';
  }
  return null;
}

function readPositiveNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function readRecordString(
  record: Record<string, unknown> | undefined,
  key: string,
): string | null {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function readEmbeddedRenderNumber(code: string, key: string): number | null {
  const match = new RegExp(`${key}:\\s*([0-9]+(?:\\.[0-9]+)?)`).exec(code);
  if (!match?.[1]) return null;
  const value = Number(match[1]);
  return readPositiveNumber(value);
}

function readRecordConst(
  code: string,
  name: string,
): Record<string, unknown> | null {
  const value = readConstJsonValue(code, name);
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readArrayConst(code: string, name: string): unknown[] {
  const value = readConstJsonValue(code, name);
  return Array.isArray(value) ? value : [];
}

function readStringConst(code: string, name: string): string | null {
  const value = readConstJsonValue(code, name);
  return typeof value === 'string' && value.trim() ? value : null;
}

function readAuthoringSolidBackgroundColor(code: string): string | null {
  const match = /backgroundColor:\s*["']([^"']+)["']/.exec(code);
  return match?.[1] ?? null;
}

function readConstJsonValue(code: string, name: string): unknown {
  const marker = `const ${name} = `;
  let markerIndex = code.indexOf(marker);
  while (markerIndex >= 0) {
    const expressionStart = skipWhitespace(code, markerIndex + marker.length);
    const expressionEnd = findJsonExpressionEnd(code, expressionStart);
    if (expressionEnd > expressionStart) {
      try {
        return JSON.parse(code.slice(expressionStart, expressionEnd));
      } catch {
        // Embedded authoring code contains this marker inside a template string
        // before the actual serialized constant. Keep scanning for the real one.
      }
    }
    markerIndex = code.indexOf(marker, markerIndex + marker.length);
  }
  return null;
}

function skipWhitespace(value: string, index: number) {
  let cursor = index;
  while (cursor < value.length && /\s/.test(value[cursor] ?? '')) {
    cursor += 1;
  }
  return cursor;
}

function findJsonExpressionEnd(value: string, start: number): number {
  const first = value[start];
  if (first === '{' || first === '[') {
    return findBalancedJsonEnd(value, start);
  }
  if (first === '"') {
    return findJsonStringEnd(value, start);
  }

  let cursor = start;
  while (cursor < value.length && value[cursor] !== ';') {
    cursor += 1;
  }
  return cursor;
}

function findBalancedJsonEnd(value: string, start: number): number {
  const stack: string[] = [];
  let inString = false;
  let escaped = false;

  for (let cursor = start; cursor < value.length; cursor += 1) {
    const char = value[cursor];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === '"') inString = false;
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }
    if (char === '{') {
      stack.push('}');
      continue;
    }
    if (char === '[') {
      stack.push(']');
      continue;
    }
    if (char !== '}' && char !== ']') continue;
    if (stack[stack.length - 1] !== char) return start;
    stack.pop();
    if (stack.length === 0) return cursor + 1;
  }

  return start;
}

function findJsonStringEnd(value: string, start: number): number {
  let escaped = false;
  for (let cursor = start + 1; cursor < value.length; cursor += 1) {
    const char = value[cursor];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === '"') return cursor + 1;
  }
  return start;
}

async function prepareAuthoringRenderRuntime(
  spec: AuthoringWritingGuideSpec,
): Promise<Record<string, unknown>> {
  if (spec.approvedReleases.length === 0) return spec.runtime;
  const approvedRuntimes = await Promise.all(
    spec.approvedReleases.map(loadApprovedWritingGuideRuntimeAsset),
  );
  if (
    spec.approvedReleases.length > 1 &&
    spec.runtime.version !== 'alphabet.authoring_runtime.v2'
  ) {
    const bindings = Array.isArray(spec.runtime.sourceReleases)
      ? spec.runtime.sourceReleases.filter(
          (value): value is Record<string, unknown> =>
            Boolean(value) &&
            typeof value === 'object' &&
            !Array.isArray(value),
        )
      : [];
    const sourceHash = await sha256Text(
      buildWritingGuideCompositeRuntimeSource(bindings),
    );
    if (sourceHash !== spec.runtime.sourceStateHash) {
      throw new Error(
        '혼합 대소문자 따라쓰기 runtime 해시가 승인본과 다릅니다.',
      );
    }
  }
  return buildApprovedWritingGuideRenderRuntime(
    spec.runtime,
    spec.word,
    spec.approvedReleases.map((release, index) => ({
      release,
      runtime: approvedRuntimes[index]!,
    })),
  );
}

function loadApprovedWritingGuideRuntimeAsset(
  release: ApprovedWritingGuideRelease,
): Promise<Record<string, unknown>> {
  const cacheKey = `${release.bundleContentHash ?? 'legacy'}:${release.runtimeAssetSourceHash}:${release.wordLayoutPolicySourceHash}`;
  const cached = approvedRuntimeAssetPromises.get(cacheKey);
  if (cached) return cached;

  const promise = (async () => {
    if (release.runtimeVersion === 'alphabet.authoring_runtime.v2') {
      return loadBundleWritingGuideRuntimeAsset(release, staticFile);
    }
    const controller=new AbortController();
    const timer=setTimeout(() => controller.abort(Error('승인된 따라쓰기 runtime 요청 시간이 초과됐습니다.')),15_000);
    try {
    const response = await fetch(staticFile(release.runtimeAssetPath), {
      cache: 'no-store',
      signal:controller.signal,
    });
    if (!response.ok) {
      throw new Error(
        `승인된 따라쓰기 runtime을 읽을 수 없습니다. (${release.releaseId}, ${response.status})`,
      );
    }
    const bytes = await readBoundedApprovedRuntimeBytes(response);
    const sourceHash = await sha256ArrayBuffer(bytes);
    if (sourceHash !== release.runtimeAssetSourceHash) {
      throw new Error(
        `따라쓰기 runtime 바이트 해시가 승인본과 다릅니다. (${release.releaseId})`,
      );
    }

    let runtime: unknown;
    try {
      runtime = JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      throw new Error(
        `승인된 따라쓰기 runtime JSON이 올바르지 않습니다. (${release.releaseId})`,
      );
    }
    if (!runtime || typeof runtime !== 'object' || Array.isArray(runtime)) {
      throw new Error(
        `승인된 따라쓰기 runtime JSON이 올바르지 않습니다. (${release.releaseId})`,
      );
    }
    const approvedRuntime = runtime as Record<string, unknown>;
    assertApprovedWritingGuideRuntimeMatchesRelease(approvedRuntime, release);
    return approvedRuntime;
    } finally { clearTimeout(timer); }
  })().catch((error: unknown) => {
    approvedRuntimeAssetPromises.delete(cacheKey);
    throw error;
  });
  approvedRuntimeAssetPromises.set(cacheKey, promise);
  return promise;
}

async function sha256ArrayBuffer(value: ArrayBuffer): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('따라쓰기 runtime 바이트 검증 기능을 사용할 수 없습니다.');
  }
  const digest = await subtle.digest('SHA-256', value);
  return `sha256:${[...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')}`;
}

async function sha256Text(value: string): Promise<string> {
  return sha256ArrayBuffer(new TextEncoder().encode(value).buffer);
}

function readAuthoringPlayback(
  expectedVersion: string | null = null,
  expectedSourceHash: string | null = null,
): AuthoringPlaybackApi | null {
  return readAlphabetWritingPlayback(
    expectedVersion,
    expectedSourceHash,
  ) as AuthoringPlaybackApi | null;
}

function frameToAuthoringProgress(
  playback: AuthoringPlaybackApi,
  currentFrame: number,
  totalFrames: number,
) {
  if (typeof playback.frameToProgress === 'function') {
    return playback.frameToProgress(currentFrame, totalFrames);
  }
  return Math.max(0, Math.min(1, currentFrame / Math.max(1, totalFrames)));
}

function loadAuthoringPlaybackScript(
  rendererSourceHash: string | null,
  rendererAssetPath: string | null,
  rendererVersion: string | null,
  embeddedRendererSource: string | null,
) {
  if (typeof document === 'undefined') return Promise.resolve();
  return loadAlphabetWritingPlayback({
    rendererSourceHash,
    rendererAssetPath,
    rendererVersion,
    fallbackAssetPath: AUTHORING_ENGINE_URL,
    embeddedRendererSource,
    resolveAssetPath: staticFile,
  }).then(() => undefined);
}
